import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Row, Col, Spin, Empty, Typography, Space, Divider, Affix, Button, Tooltip, Modal, notification } from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuthZ } from '../../authZ/useAuthZ';
import PageContent from '../../structure/PageContent';
import { useNotificationsLogic } from '../useNotificationsLogic';
import CreateNotificationModal from './CreateNotificationModal';
import EditNotificationModal from './EditNotificationModal';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';

const Notifications = () => {
	dayjs.extend(utc);

	const authZ = useAuthZ();
	const notificationsLogic = useNotificationsLogic();

	//-- Used for translations of text
	const { t } = useI18nLocalization();

	//-- Data source
	const [notifications, setNotifications] = useState(null);
	const [selectedNotification, setSelectedNotification] = useState(null);

	const [createNotificationModalIsVisible, setCreateNotificationModalIsVisible] = useState(false);
	const [editNotificationModalIsVisible, setEditNotificationModalIsVisible] = useState(false);

	useEffect(() => {
		if (!notifications) {
			getNotifications();
		}
	}, []);

	//-- Get notifications from the API
	const getNotifications = async () => {
		let nots = await notificationsLogic.getNotifications();

		nots = _.map(nots, (n) => {
			if (n.enabled && dayjs().isSameOrBefore(dayjs(n.notifyThroughDateUtc))) {
				return _.extend({}, n, { status: 'active' });
			} else {
				return _.extend({}, n, { status: 'inactive' });
			}
		});

		setNotifications(nots);
	};

	const deleteNotification = async (title, notifyThroughDateUtc) => {
		const successful = await notificationsLogic.deleteNotification(title, notifyThroughDateUtc);

		if (successful) {
			notificationsLogic.refreshCurrentNotifications(); //-- Update displayed notifications
			getNotifications(); //-- Get new list for notifications page

			notification['success']({
				message: t(`NotificationDeleted`)
			});
		} else {
			notification['error']({
				message: t('DeleteNotificationError'),
				duration: 10
			});
		}
	};

	return !notifications ? (
		<Row justify='center' align='middle' style={{ height: '100%' }}>
			<Col>
				<Spin />
			</Col>
		</Row>
	) : !notifications.length ? (
		<>
			<Row justify='center' align='middle' style={{ height: '100%' }}>
				<Col>
					<Empty description={<span>{t('NoNotifications')}</span>} />
				</Col>
			</Row>
			{renderNewEditButton(
				authZ,
				createNotificationModalIsVisible,
				setCreateNotificationModalIsVisible,
				getNotifications,
				notificationsLogic,
				setSelectedNotification,
				editNotificationModalIsVisible,
				setEditNotificationModalIsVisible,
				selectedNotification,
				t
			)}
		</>
	) : (
		<>
			{_.map(notifications, (n) => {
				return (
					<PageContent key={n.id} style={{ marginBottom: '15px' }}>
						<>
							<Space direction='vertical' style={{ float: 'left', maxWidth: '750px' }}>
								<Typography.Title level={3} style={{ color: n.status === 'active' ? '#008000' : '#8B0000' }}>
									{n.title}
								</Typography.Title>
								{authZ.userIsEntDevAdmin() && (
									<>
										<Space direction='horizontal' wrap={true} split={<span>|</span>}>
											<span>
												{t('Enabled')}:{' '}
												<span style={{ fontStyle: 'italic' }}>{n.enabled ? t('True') : t('False')}</span>
											</span>
											<span>
												{t('Type')}: <span style={{ fontStyle: 'italic' }}>{t(n.notificationType)}</span>
											</span>
											<span>
												{t('DisplayType')}:{' '}
												<span style={{ fontStyle: 'italic' }}>{n.banner ? t('Banner') : t('Toast')}</span>
											</span>
											<span>
												{t('CreatedDate')}:{' '}
												<span style={{ fontStyle: 'italic' }}>
													{t('{{date}}', { date: dayjs(n.createdDateTimeUtc).utc().local() })}
												</span>
											</span>
											{n.banner && (
												<>
													<span>
														{t('Dismissable')}:{' '}
														<span style={{ fontStyle: 'italic' }}>{n.dismissable ? t('True') : t('False')}</span>
													</span>
												</>
											)}
											{n.applicationId && (
												<>
													<span>
														{t('Application')}: <span style={{ fontStyle: 'italic' }}>{n.applicationName}</span>
													</span>
												</>
											)}
										</Space>
									</>
								)}
							</Space>
							{authZ.userIsEntDevAdmin() && (
								<>
									<Space direction='horizontal' style={{ marginTop: '10px', float: 'right', paddingLeft: '25px' }}>
										<i
											className='las la-pencil-alt la-lg cursor-pointer'
											onClick={() => {
												setSelectedNotification(n);
												setEditNotificationModalIsVisible(true);
											}}
										></i>
										<i
											className='las la-trash la-lg cursor-pointer'
											onClick={() => {
												Modal.confirm({
													title: `${t('Delete')} '${n.title}'`,
													icon: <ExclamationCircleOutlined />,
													content: `${t('ConfirmDelete')} ${t('This action cannot be undone')}.`,
													okText: `${t('Delete')}`,
													cancelText: `${t('Cancel')}`,
													onOk() {
														deleteNotification(n.title, n.notifyThroughDateUtc);
													}
												});
											}}
										></i>
									</Space>
								</>
							)}
							<Typography.Title level={5} italic style={{ marginTop: '10px', float: 'right' }}>
								{t('{{date}}', { date: dayjs(n.notifyThroughDateUtc).utc().local() })}
							</Typography.Title>
							<Divider style={{ margin: '0px 0px 15px 0px' }} />
							<Typography.Paragraph style={{ marginBottom: 'unset', whiteSpace: 'pre-wrap' }}>
								{n.message}
							</Typography.Paragraph>
						</>
					</PageContent>
				);
			})}
			{renderNewEditButton(
				authZ,
				createNotificationModalIsVisible,
				setCreateNotificationModalIsVisible,
				getNotifications,
				notificationsLogic,
				setSelectedNotification,
				editNotificationModalIsVisible,
				setEditNotificationModalIsVisible,
				selectedNotification,
				t
			)}
		</>
	);
};

const renderNewEditButton = (
	authZ,
	createNotificationModalIsVisible,
	setCreateNotificationModalIsVisible,
	getNotifications,
	notificationsLogic,
	setSelectedNotification,
	editNotificationModalIsVisible,
	setEditNotificationModalIsVisible,
	selectedNotification,
	t
) => {
	return (
		authZ.userIsEntDevAdmin() && (
			<>
				<Affix style={{ position: 'absolute', bottom: '7%', right: '2%' }}>
					<Tooltip title={t('CreateNewNotification')} placement='left'>
						<Button
							type='primary'
							shape='circle'
							size={'large'}
							icon={<PlusOutlined />}
							style={{ boxShadow: '2px 2px 5px grey' }}
							onClick={() => {
								setCreateNotificationModalIsVisible(true);
							}}
						/>
					</Tooltip>
				</Affix>
				<CreateNotificationModal
					isVisible={createNotificationModalIsVisible}
					onSaveCallback={() => {
						getNotifications();
						notificationsLogic.refreshCurrentNotifications();
						setCreateNotificationModalIsVisible(false);
						setSelectedNotification(null);
					}}
					onCloseCallback={() => {
						setCreateNotificationModalIsVisible(false);
						setSelectedNotification(null);
					}}
				/>
				<EditNotificationModal
					isVisible={editNotificationModalIsVisible}
					onSaveCallback={() => {
						getNotifications();
						notificationsLogic.refreshCurrentNotifications();
						setEditNotificationModalIsVisible(false);
						setSelectedNotification(null);
					}}
					onCloseCallback={() => {
						setEditNotificationModalIsVisible(false);
						setSelectedNotification(null);
					}}
					ntificn={selectedNotification}
				/>
			</>
		)
	);
};

export default Notifications;
